import {ChangeDetectionStrategy, Component} from '@angular/core';
import {ContentService} from "../_services/content.service";
import {AuthService} from "../_services/auth.service";
import {environment} from "../../environments/environment";
import {User} from "../_types/user";

@Component({
    selector: 'app-status-page',
    templateUrl: './status-page.component.html',
    styleUrl: './status-page.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StatusPageComponent {
    cacheBackend: boolean | null = null;
    databaseBackend: boolean | null = null;
    defaultFileStorageHealthCheck: boolean | null = null;
    diskUsage: boolean | null = null;
    memoryUsage: boolean | null = null;
    migrationsHealthCheck: boolean | null = null;
    redisHealthCheck: boolean | null = null;
    s3Boto3StorageHealthCheck: boolean | null = null;
    trainingContentAPI: boolean | null = null;
    mail: boolean | null = null;
    raa: boolean | null = null;
    isRaa: boolean = false;
    browserCheck: string | null = null;
    clientTimeCheck: boolean | null = null;
    clientTime: string | null = null;
    clientTimezone: string | null = null;
    mediaPermissionCheck: string | null = null;
    userAgent: string | null = null;
    currentUser: User | undefined;

    constructor(private contentService: ContentService,
                private authService: AuthService) {
        this.authService.getDynamicSettings().then((r): void => {
            this.isRaa = r.is_raa;
        });

        this.authService.getUser().subscribe({
            next: r => {
                this.currentUser = r;
            }
        })
        this.userAgent = navigator.userAgent;
        this.contentService.healthCheck().subscribe({
            next: r => {
                this.checkValues(r)
            }, error: error => {
                let objects = error;
                if (!error.hasOwnProperty("DatabaseBackend")) {
                    objects = {
                        "Cache": "failed",
                        "Database": "failed",
                        "DNS": "failed",
                        "Disk": "failed",
                        "Memory": "failed",
                        "Migrations": "failed",
                        "Redis": "failed",
                        "S3 Configuration": "failed",
                        "Mail": "failed",
                        "RAA Configuration": "failed",
                    }
                }

                this.checkValues(objects)
            }
        });

        this.contentService.videoGuides("adult").subscribe({
            next: r => {
                this.trainingContentAPI = true;
            }, error: error => {
                this.trainingContentAPI = false;
            }
        });
        void this.checkBrowser();
        this.checkClientTime();
        this.checkMediaPermissions();
    }

    async checkBrowser(): Promise<void> {
        if (!await this.checkImage()) {
            this.browserCheck = 'Local assets unavailable';
        } else if (!navigator.onLine) {
            this.browserCheck = 'Browser is offline';
            return;
        } else if (!this.localStorageAvailable()) {
            this.browserCheck = 'Local storage is offline';
            return;
        } else {
            this.browserCheck = 'success';
        }
    }

    checkClientTime(): void {
        const now = new Date();

        if (Number.isNaN(now.getTime())) {
            this.clientTimeCheck = false;
            this.clientTime = 'Unavailable';
            this.clientTimezone = 'Unavailable';
            return;
        }

        this.clientTimeCheck = true;
        this.clientTime = new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'medium',
        }).format(now);

        const timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown timezone';
        this.clientTimezone = `${timezone} (${this.formatTimezoneOffset(now.getTimezoneOffset())})`;
    }

    checkImage(src: string = 'assets/images/vmb.svg'): Promise<boolean> {
        return new Promise((resolve): void => {
            const img: HTMLImageElement = new Image();

            img.onload = (): void => {
                resolve(true);
            };

            img.onerror = (): void => {
                resolve(false);
            };

            img.src = src;

            if (img.complete) {
                resolve(img.naturalWidth > 0);
            }
        });
    }

    localStorageAvailable(): boolean {
        try {
            const testKey: string = 'vmb-status-page-test';
            window.localStorage.setItem(testKey, testKey);
            window.localStorage.removeItem(testKey);
            return true;
        } catch {
            return false;
        }
    }

    formatTimezoneOffset(offsetMinutes: number): string {
        const sign: string = offsetMinutes <= 0 ? '+' : '-';
        const absoluteOffset: number = Math.abs(offsetMinutes);
        const hours: string = Math.floor(absoluteOffset / 60).toString().padStart(2, '0');
        const minutes: string = (absoluteOffset % 60).toString().padStart(2, '0');

        return `UTC${sign}${hours}:${minutes}`;
    }


    async checkMediaPermissions(): Promise<void> {
        try {
            if (!navigator.permissions) {
                this.mediaPermissionCheck = 'Permission checks unavailable';
                return;
            }

            const cameraPermission: PermissionStatus = await navigator.permissions.query(<PermissionDescriptor><unknown>{
                name: 'camera',
                requestedOrigin: window.location.origin
            });
            if (cameraPermission.state === 'denied') {
                this.mediaPermissionCheck = 'No camera access';
                return;
            }

            const microphonePermission: PermissionStatus = await navigator.permissions.query(<PermissionDescriptor><unknown>{
                name: 'microphone',
                requestedOrigin: window.location.origin
            });
            if (microphonePermission.state === 'denied') {
                this.mediaPermissionCheck = 'No microphone access';
                return;
            }

            this.mediaPermissionCheck = 'success';
        } catch (error) {
            console.error('Error querying permissions:', error);
            this.mediaPermissionCheck = 'Permission checks unavailable';
        }
    }

    checkValues(response: any): void {
        for (const [key, value] of Object.entries(response)) {
            const lowerKey: string = key.toLowerCase();
            const isOk: boolean = value === "OK";

            switch (true) {
                case lowerKey.includes('cache'):
                    this.cacheBackend = isOk;
                    break
                case lowerKey.includes('database'):
                    this.databaseBackend = isOk;
                    break
                case lowerKey.includes('dns'):
                    this.defaultFileStorageHealthCheck = isOk;
                    break
                case lowerKey.includes('disk'):
                    this.diskUsage = isOk;
                    break
                case lowerKey.includes('memory'):
                    this.memoryUsage = isOk;
                    break
                case lowerKey.includes('migrations'):
                    this.migrationsHealthCheck = isOk;
                    break
                case lowerKey.includes('redis'):
                    this.redisHealthCheck = isOk;
                    break
                case lowerKey.includes('s3'):
                    this.s3Boto3StorageHealthCheck = isOk;
                    break
                case lowerKey.includes('mail'):
                    this.mail = isOk;
                    break
                case lowerKey.includes('raa'):
                    this.raa = isOk;
                    break
            }
        }
    }

    printInfo(): void {
        window.print();
    }

    protected readonly environment = environment;
}
